import { useEffect, useRef } from "react";
import {
  applyLeadUserVarsFromDecrypt,
  AUTH_OUTCOME,
  LEAD_DESTINATION,
  type LeadDecryptPayload,
  type LeadEntryRoute,
  normalizeLeadProviderForEvent,
  resolveLeadStayDestination,
  trackFSLeadDecryptCompleted,
  trackFSLeadLinkOpened,
  trackFSLeadRouted,
  mapLeadBorrowerType,
  toAuthFailureCode,
} from "utilities/full-story";

export function useLeadLinkOpened(
  entryRoute: LeadEntryRoute,
  options: { leadProvider?: string | null; hasEncryptedToken: boolean }
) {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackFSLeadLinkOpened({
      entryRoute,
      leadProvider: options.leadProvider,
      hasEncryptedToken: options.hasEncryptedToken,
    });
  }, [entryRoute, options.hasEncryptedToken, options.leadProvider]);
}

export interface UseLeadDecryptTrackingOptions {
  entryRoute: LeadEntryRoute;
  skip: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: unknown;
  data?: LeadDecryptPayload | null;
  leadProviderFromUrl?: string | null;
  leadCampaign?: string | null;
}

export function useLeadDecryptTracking({
  entryRoute,
  skip,
  isLoading,
  isFetching,
  isSuccess,
  isError,
  error,
  data,
  leadProviderFromUrl,
  leadCampaign,
}: UseLeadDecryptTrackingOptions) {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (skip || trackedRef.current) return;
    if (isLoading || isFetching) return;
    if (!isSuccess && !isError) return;

    trackedRef.current = true;
    const leadProviderFallback = normalizeLeadProviderForEvent(leadProviderFromUrl);

    if (isError || !data) {
      trackFSLeadDecryptCompleted({
        entryRoute,
        outcome: AUTH_OUTCOME.failure,
        isExpired: true,
        leadProvider: leadProviderFallback,
        failureCode: toAuthFailureCode(error),
      });
      trackFSLeadRouted({
        entryRoute,
        destination: LEAD_DESTINATION.error,
        leadProvider: leadProviderFallback,
      });
      return;
    }

    const leadProvider = normalizeLeadProviderForEvent(data.lpn ?? leadProviderFromUrl);
    applyLeadUserVarsFromDecrypt(data, leadProviderFromUrl);

    trackFSLeadDecryptCompleted({
      entryRoute,
      outcome: AUTH_OUTCOME.success,
      isExpired: false,
      leadProvider,
      leadCampaign: leadCampaign ?? undefined,
      loanType: data.lp ?? undefined,
      borrowerType: mapLeadBorrowerType(data.rt),
    });

    const destination = data.ex ? LEAD_DESTINATION.login : resolveLeadStayDestination(entryRoute);
    trackFSLeadRouted({
      entryRoute,
      destination,
      leadProvider,
    });
  }, [
    data,
    entryRoute,
    error,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    leadCampaign,
    leadProviderFromUrl,
    skip,
  ]);
}
